/**
 * Reservation status machine — the single source of VALID transitions in the UI.
 *
 * Invalid transitions are NEVER offered (the backend remains authoritative, but
 * the UI must not present moves it knows are illegal). The lifecycle is a sales
 * funnel: lead intake → offer drafting → offer outcome → terminal. See
 * docs/architecture/03-hostesa-app/12-reservation-pipeline.md and
 * docs/architecture/02-hostesa-backend/18-reservation-lead-pipeline.md.
 */

import type { ReservationStatus, StatusVariant } from './_deps';

/** Ordered pipeline stages for the board columns. */
export const PIPELINE_STAGES: readonly ReservationStatus[] = [
  'new',
  'missing_information',
  'offer_needed',
  'offer_drafted',
  'offer_sent',
  'waiting_for_guest',
  'converted',
  'lost',
  'cancelled',
] as const;

/** Statuses where an offer draft editor is relevant. */
export const OFFER_STAGES: readonly ReservationStatus[] = [
  'offer_needed',
  'offer_drafted',
  'offer_sent',
  'waiting_for_guest',
] as const;

/** Map each status to the set of statuses it may legally transition to. */
const TRANSITIONS: Record<ReservationStatus, readonly ReservationStatus[]> = {
  new: ['missing_information', 'offer_needed', 'lost', 'cancelled'],
  missing_information: ['offer_needed', 'lost', 'cancelled'],
  offer_needed: ['offer_drafted', 'missing_information', 'lost', 'cancelled'],
  offer_drafted: ['offer_sent', 'offer_needed', 'lost', 'cancelled'],
  offer_sent: ['waiting_for_guest', 'converted', 'lost', 'cancelled'],
  waiting_for_guest: ['converted', 'lost', 'cancelled'],
  converted: [],
  lost: [],
  cancelled: [],
};

/** Valid next statuses from the given current status (never includes itself). */
export function validTransitions(from: ReservationStatus): readonly ReservationStatus[] {
  return TRANSITIONS[from];
}

/** Whether moving `from` → `to` is a permitted transition. */
export function canTransition(from: ReservationStatus, to: ReservationStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

/** Visual (non-color-only) status variant for the StatusBadge. */
export function reservationStatusVariant(status: ReservationStatus): StatusVariant {
  switch (status) {
    case 'new':
      return 'neutral';
    case 'missing_information':
    case 'offer_needed':
      return 'warning';
    case 'offer_drafted':
    case 'offer_sent':
      return 'pending';
    case 'waiting_for_guest':
      return 'pending';
    case 'converted':
      return 'success';
    case 'lost':
    case 'cancelled':
      return 'inactive';
  }
}
